import React, { useEffect, useMemo, useState } from "react";
import api from '../../services/apiClient.ts';
import { toast } from "react-toastify";
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';

const SalesPage = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();

  const [groups, setGroups] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    gr_id: "",
    su_id: "",
    price: "",
    cost_of_seats: "",
    sale_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [editingId, setEditingId] = useState(null);

  const [filters, setFilters] = useState({
    gr_id: "",
    su_id: "",
    date_from: "",
    date_to: "",
  });
  const [searchQuery, setSearchQuery] = useState("");

  const filteredSales = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return sales;
    return sales.filter(s =>
      String(s.group_name || "").toLowerCase().includes(q) ||
      String(s.supervisor_name || "").toLowerCase().includes(q) ||
      String(s.notes || "").toLowerCase().includes(q)
    );
  }, [sales, searchQuery]);

  const loadMeta = async () => {
    try {
      const reqHeaders = {
        Accept: 'application/json',
        ...(localStorage.getItem('tenant_key') ? { 'X-Tenant': localStorage.getItem('tenant_key') } : {}),
        ...(localStorage.getItem('branch_id') ? { 'X-Branch': localStorage.getItem('branch_id') } : {}),
        ...(localStorage.getItem('auth_token') ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` } : {}),
      };


      // Fetch ALL groups (not only active) from /v1/groups
      const groupsResp = await api.get("/v1/groups", {
        params: { per_page: 500 },
        headers: reqHeaders,
      });
      const rawGroups = groupsResp.data?.data || groupsResp.data || [];
      const mappedGroups = rawGroups.map((g) => ({
        id: g.id,
        group_name: g.gr_notes || g.group_name || g.name || '',
        cost: typeof g.gr_out !== 'undefined' ? Number(g.gr_out) : Number(g.cost || 0),
      }));
      setGroups(mappedGroups);
      
      
      // Fetch groups and supervisors from sales meta (backend provides active groups per branch/tenant)
      const metaResp = await api.get("/v1/sales/meta", { headers: reqHeaders });
      //setGroups(metaResp.data?.groups || []);
      setSupervisors(metaResp.data?.supervisors || []);
    } catch (err) {
      console.error(err);
      toast.error(t('sales.metaLoadError'));
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const loadSales = async () => {
    try {
      const params = {};
      if (filters.gr_id) params.gr_id = filters.gr_id;
      if (filters.su_id) params.su_id = filters.su_id;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const reqHeaders = {
        Accept: 'application/json',
        ...(localStorage.getItem('tenant_key') ? { 'X-Tenant': localStorage.getItem('tenant_key') } : {}),
        ...(localStorage.getItem('branch_id') ? { 'X-Branch': localStorage.getItem('branch_id') } : {}),
        ...(localStorage.getItem('auth_token') ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` } : {}),
      };
      console.log('[SalesPage] loadSales request', {
        url: `${api.defaults.baseURL}/v1/sales`, params, headers: reqHeaders,
      });
      const resp = await api.get("/v1/sales", { params, headers: reqHeaders });
      const data = resp?.data;
      console.log('[SalesPage] loadSales response', {
        status: resp?.status, keys: data && Object.keys(data), sample: Array.isArray(data) ? data.slice(0,1) : data?.data?.slice?.(0,1),
      });
      setSales(data?.data || data || []);
    } catch (err) {
      console.error(err);
      toast.error(t('sales.salesLoadError'));
    }
  };

  useEffect(() => {
    loadMeta();
    loadSales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleGroupChange = (e) => {
    const value = e.target.value;
    const selectedGroup = groups.find((g) => String(g.id) === value);
    setForm((prev) => ({
      ...prev,
      gr_id: value,
      cost_of_seats: selectedGroup ? selectedGroup.cost : prev.cost_of_seats,
    }));
  };

  const resetForm = () => {
    setForm({
      gr_id: "",
      su_id: "",
      price: "",
      cost_of_seats: "",
      sale_date: new Date().toISOString().slice(0, 10),
      notes: "",
    });
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.gr_id || !form.su_id || !form.price ) {
      return toast.error(t('sales.requiredFields'));
    }
    setLoading(true);
    try {
      const payload = {
        gr_id: Number(form.gr_id),
        su_id: Number(form.su_id),
        price: Number(form.price),
        cost_of_seats: Number(form.cost_of_seats),
        sale_date: form.sale_date,
        notes: (form.notes || '').trim(),
      };
      const reqHeaders = {
        Accept: 'application/json',
        ...(localStorage.getItem('tenant_key') ? { 'X-Tenant': localStorage.getItem('tenant_key') } : {}),
        ...(localStorage.getItem('branch_id') ? { 'X-Branch': localStorage.getItem('branch_id') } : {}),
        ...(localStorage.getItem('auth_token') ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` } : {}),
      };
      if (editingId) {
        await api.put(`/v1/sales/${editingId}`, payload, { headers: reqHeaders });
        toast.success(t('sales.updateSuccess'));
      } else {
        await api.post("/v1/sales", payload, { headers: reqHeaders });
        toast.success(t('sales.saveSuccess'));
      }
      await loadSales();
      await loadMeta();
      resetForm();
    } catch (err) {
      console.error(err);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (sale) => {
    setEditingId(sale.id);
    // Prefer backend IDs if present; fallback to name matching
    const groupId = sale.gr_id ?? sale.group_id ?? (groups.find((g) => g.group_name === sale.group_name)?.id || "");
    const supervisorId = sale.su_id ?? sale.supervisor_id ?? (supervisors.find((s) => s.supervisor_name === sale.supervisor_name)?.id || "");
    const selectedGroup = groups.find((g) => String(g.id) === String(groupId));
    setForm({
      gr_id: groupId ? String(groupId) : "",
      su_id: supervisorId ? String(supervisorId) : "",
      price: String(sale.price ?? ""),
      cost_of_seats: String(sale.cost_of_seats ?? (selectedGroup ? selectedGroup.cost : "")),
      sale_date: sale.sale_date || new Date().toISOString().slice(0, 10),
      notes: sale.notes || "",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('sales.deleteConfirm'))) return;
    try {
      const reqHeaders = {
        Accept: 'application/json',
        ...(localStorage.getItem('tenant_key') ? { 'X-Tenant': localStorage.getItem('tenant_key') } : {}),
        ...(localStorage.getItem('branch_id') ? { 'X-Branch': localStorage.getItem('branch_id') } : {}),
        ...(localStorage.getItem('auth_token') ? { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` } : {}),
      };
      await api.delete(`/v1/sales/${id}`, { headers: reqHeaders });
      toast.success(t('sales.deleteSuccess'));
      await loadSales();
      await loadMeta();
    } catch (err) {
      console.error(err);
      toast.error(t('sales.deleteError'));
    }
  };

  return (
    <div className="text-gray-900 dark:text-gray-100 py-6 px-4 sm:px-6 lg:px-8" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 mb-8 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-6">
            <div className="flex items-center space-x-reverse space-x-3">
              <div className="bg-white/20 rounded-full p-3">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h4l2 5h8l2-5h4M5 21h14" />
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{t('sales.title')}</h2>
                <p className="text-blue-100 mt-1">{t('sales.subtitle')}</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('sales.saleDate')}</label>
                <input
                  type="date"
                  name="sale_date"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  value={form.sale_date}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('sales.groupName')}</label>
                <select
                  name="gr_id"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  value={form.gr_id}
                  onChange={handleGroupChange}
                >
                  <option value="">{t('common.select') || t('sales.all')}</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.group_name} {g.cost ? `(${t('sales.costOfSeats')}: ${g.cost})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('sales.supervisorName')}</label>
                <select
                  name="su_id"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  value={form.su_id}
                  onChange={handleChange}
                >
                  <option value="">{t('common.select') || t('sales.all')}</option>
                  {supervisors.map((s) => (
                    <option key={s.id} value={s.id}>{s.supervisor_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('sales.price')}</label>
                <input
                  type="number"
                  step="0.01"
                  name="price"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  value={form.price}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('sales.costOfSeats')}</label>
                <input
                  type="number"
                  step="0.01"
                  name="cost_of_seats"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  value={form.cost_of_seats}
                  onChange={handleChange}
                />
              </div>
              <div className="md:col-span-3">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-2">{t('sales.notes')}</label>
                <textarea
                  name="notes"
                  rows={2}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  value={form.notes}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="mt-6 flex items-center space-x-reverse space-x-3">
              <button type="submit" disabled={loading} className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-all duration-200 hover:scale-[1.02]">
                {editingId ? t('sales.updateSale') : t('sales.addSale')}
              </button>
              {editingId && (
                <button type="button" onClick={resetForm} className="px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all">
                  {t('sales.cancelEdit')}
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Table with merged Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-fuchsia-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">{t('sales.tableTitle')}</h3>
            </div>
          </div>
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">{t('sales.groupName')}</label>
                <select name="gr_id" value={filters.gr_id} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500">
                  <option value="">{t('sales.all')}</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.group_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">{t('sales.supervisorName')}</label>
                <select name="su_id" value={filters.su_id} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500">
                  <option value="">{t('sales.all')}</option>
                  {supervisors.map((s) => (
                    <option key={s.id} value={s.id}>{s.supervisor_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">{t('common.startDate') || t('sales.dateFrom')}</label>
                <input type="date" name="date_from" value={filters.date_from} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">{t('common.endDate') || t('sales.dateTo')}</label>
                <input type="date" name="date_to" value={filters.date_to} onChange={handleFilterChange} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-200 mb-1">{t('common.search') || 'Search'}</label>
                <input type="text" value={searchQuery} onChange={(e)=>setSearchQuery(e.target.value)} placeholder={t('common.search') || 'Search...'} className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-indigo-500" />
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={loadSales} className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold transition-all duration-200 hover:scale-[1.02]">
                  {t('sales.applyFilter')}
                </button>
              </div>
            </div>
          </div>
          <div className="p-6">
          <div className="app-table-wrap">
            <table className="app-table min-w-full">
              <thead className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-700 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider">{t('sales.index')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider">{t('sales.groupName')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider">{t('sales.supervisorName')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider">{t('sales.price')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider">{t('sales.costOfSeats')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider">{t('sales.date')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider">{t('sales.notes')}</th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 dark:text-gray-200 uppercase tracking-wider">{t('sales.actions') || t('common.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredSales.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-6 py-12 text-center text-gray-500">{t('common.noData') || 'No records found'}</td>
                  </tr>
                ) : (
                  filteredSales.map((s, idx) => (
                    <tr key={s.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{idx + 1}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{s.group_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{s.supervisor_name}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{Number(s.price || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{Number(s.cost_of_seats || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} د.ع</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{s.sale_date}</td>
                      <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100">{s.notes}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex items-center justify-center space-x-reverse space-x-2">
                          <button className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-indigo-700 dark:text-indigo-300 transition-colors" onClick={() => handleEdit(s)}>
                            {t('common.edit')}
                          </button>
                          <button className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-red-700 dark:text-red-300 transition-colors" onClick={() => handleDelete(s.id)}>
                            {t('common.delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesPage;
